import React, { useMemo, useState } from 'react'
import { Box, Card, CardContent, Chip, Typography, useTheme } from '@mui/material'
import { black, green } from '../../../../colors'
import { notification } from '../../../../components/Notifications'
import GenericButton from '../../../../components/GenericButton'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { useMutation } from '@tanstack/react-query'
import CommentsSummarization, { type Summarization } from './CommentsSummarization'
import { FlaggingAndSummarizationManager } from '../../../../APIManager'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import Dropdown from '../../../../components/Dropdown'

import CommentList from './CommentList'
import { type ReportWithComments } from '../../../../models/Report'
import CustomTooltip from '../../../../components/CustomTooltip'
import GenericModal from '../../../../components/GenericModal'
import { Link } from 'react-router-dom'
import QuestionSampleSizeRow from '../QuestionSampleSizeRow'
import { getQuestionSampleStats } from '../questionSampleSize'

export interface ToxicityResult {
  text: string
  toxicityLabels: string[]
}

/** The summarization service returns either the category list or an error envelope. */
type SummarizationResponse = Summarization[] | { error: string }

const MIN_COMMENTS_TO_SUMMARIZE = 10
const MAX_COMMENTS_PER_SUMMARY = 100

const SUMMARIZATION_ERROR_MESSAGE = 'Some comments were too long to summarize. Please try again, or contact support@classranked.com if the problem continues.'
const SUMMARIZATION_FAILED_NOTIFICATION = 'AI summarization failed. Please try again.'
const SUMMARIZATION_EMPTY_NOTIFICATION = 'No summary could be generated from these comments. Please try again.'

export interface CommentCardProps {
  title: string
  report: ReportWithComments
  titles?: string[]
  disableFlaggedCommentActions?: boolean
}

const CommentCard: React.FC<CommentCardProps> = ({ title, report, titles = [], disableFlaggedCommentActions = false }) => {
  const comments = report?.comments?.[0] ?? []
  const [showFlagged, setShowFlagged] = useState(false)
  const [showSummarizationErrorModal, setShowSummarizationErrorModal] = useState(false)
  const [hasSummarizationError, setHasSummarizationError] = useState(false)
  const theme = useTheme()
  const questionSampleStats = getQuestionSampleStats({ report, titles })

  const validComments: string[] = useMemo(() => {
    return comments
      .filter((comment): comment is string => typeof comment === 'string' && comment.trim().length > 0)
  }, [comments])

  const handleFlaggedResponsesToggle = (action: boolean): void => {
    setShowFlagged(action)
    notification(
      action
        ? 'Flagged responses are now visible.'
        : 'Flagged responses are now hidden.',
      { variant: 'info' }
    )
  }

  const flaggedResponsesDropdownMenu = [
    {
      text: 'Show Flagged Responses',
      action: () => { handleFlaggedResponsesToggle(true) },
      visible: !showFlagged
    },
    {
      text: 'Hide Flagged Responses',
      action: () => { handleFlaggedResponsesToggle(false) },
      visible: showFlagged
    }
  ]
  const commentsToSummarize = validComments.slice(0, MAX_COMMENTS_PER_SUMMARY)

  const { mutate: summarizeMutation, data: summarizationResult, isPending: summarizationLoading } = useMutation<SummarizationResponse>({
    mutationFn: async () => {
      const response = await FlaggingAndSummarizationManager.commentsSummarization(commentsToSummarize)
      return response.data
    },
    onSuccess: (data) => {
      if (!Array.isArray(data)) {
        notification(SUMMARIZATION_FAILED_NOTIFICATION, { variant: 'error' })
        setHasSummarizationError(true)
        setShowSummarizationErrorModal(true)
        return
      }
      if (data.length === 0) {
        notification(SUMMARIZATION_EMPTY_NOTIFICATION, { variant: 'warning' })
        return
      }
      notification('Successfully summarized comments', { variant: 'success' })
    },
    onError: (error: any) => {
      notification(
        `Failed to summarize comments${error?.message ? `: ${error.message}` : ''}`,
        { variant: 'error' }
      )
    }
  })

  // A previous failure only explains itself in the tooltip — it must not block a retry.
  const handleSummarization = (): void => {
    setHasSummarizationError(false)
    summarizeMutation()
  }

  // Only a category list counts as a rendered summary. An error envelope or an empty
  // list leaves the button in place so the user can try again.
  const summaries = Array.isArray(summarizationResult) ? summarizationResult : null
  const hasSummaries = summaries !== null && summaries.length > 0

  const isSummarizationDisabled = validComments.length < MIN_COMMENTS_TO_SUMMARIZE
  // Helper for summarization tooltip
  const getSummarizationTooltip = (): React.ReactNode => {
    switch (true) {
      case hasSummarizationError:
        return SUMMARIZATION_ERROR_MESSAGE
      case isSummarizationDisabled:
        return '10 or more comments are needed to summarize with AI'
      case validComments.length >= MAX_COMMENTS_PER_SUMMARY:
        return (
          <span>
            Currently, Our system supports up to 100 comments per summary. If you need to summarize more than 100 comments, please contact Classranked support team for assistance.<br />
            <Link style={{ color: green[0], textDecoration: 'underline' }} to="mailto:support@classranked.com">
              support@classranked.com
            </Link>
          </span>
        )
      default:
        return 'Generate a summary of the comments using AI'
    }
  }

  return (
    <Card sx={{ px: theme.spacing(1), mt: 3, mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2 }}>
          <Box display="flex" flexDirection="column" gap={1} maxWidth="70%">
            <Typography variant="h6" color={black[100]}>{title}</Typography>
            <QuestionSampleSizeRow stats={questionSampleStats} />
          </Box>
          <Box display={'flex'} gap={2} alignItems="center">
            {
              hasSummaries
                ? <Chip
                  label={'AI-enhanced insights'}
                  icon={<AutoAwesomeOutlinedIcon sx={{ color: `${green[100]} !important` }} />}
                  sx={{
                    fontWeight: '600',
                    fontSize: '14px',
                    background: green[0],
                    color: green[100],
                    border: `1px solid ${green[50]}`,
                    borderRadius: 1
                  }}
                />
                : <CustomTooltip
                  title={getSummarizationTooltip()}
                >
                  <GenericButton
                    startIcon={<AutoAwesomeOutlinedIcon />}
                    loading={summarizationLoading}
                    label='Summarize with AI'
                    onClick={handleSummarization}
                    buttonType={ButtonType.Secondary}
                    fontWeight='600'
                    disabled={isSummarizationDisabled}
                    sx={{ minWidth: '200px', fontWeight: '600' }}
                  />
                </CustomTooltip>
            }

            {validComments.length > 0 && !disableFlaggedCommentActions &&
              <Dropdown
                dropdownType='action'
                menuItems={flaggedResponsesDropdownMenu}
                extraButtonProps={{
                  buttonType: ButtonType.Ghost,
                  sx: { padding: 0 }
                }}
              />
            }
          </Box>
        </Box>

        {
          summaries !== null &&
          <CommentsSummarization summarization={summaries} />
        }

        {
          <CommentList
            results={validComments}
            showFlagged={showFlagged}
            report={report}
            disableFlaggedCommentActions={disableFlaggedCommentActions}
          />
        }

        <GenericModal
          destructiveButtonType={ButtonType.Primary}
          destructiveButtonText='Close'
          hideConfirmButton
          isOpen={showSummarizationErrorModal}
          maxWidth='sm'
          onClose={() => { setShowSummarizationErrorModal(false) }}
          title='AI summarization failed'
        >
          <Typography sx={{ mb: 2 }}>
            {SUMMARIZATION_ERROR_MESSAGE}
          </Typography>
        </GenericModal>
      </CardContent>
    </Card>
  )
}

export default CommentCard
